import { useState } from 'react'
import {
  useHardwarePresets,
  useCreateHardwarePreset,
  useUpdateHardwarePreset,
  useDeleteHardwarePreset,
} from '../hooks/useHardwarePresets'
import { BackLink } from '../components/BackLink'
import { Card, Skeleton } from '../components/ui'
import { formatMoney } from '../lib/format'

export default function HardwareLibrary() {
  const { data: presets, isLoading } = useHardwarePresets()
  const create = useCreateHardwarePreset()
  const update = useUpdateHardwarePreset()
  const del = useDeleteHardwarePreset()

  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [qty, setQty] = useState('1')
  const [unitCost, setUnitCost] = useState('')

  const resetForm = () => {
    setName('')
    setQty('1')
    setUnitCost('')
    setAdding(false)
    setEditingId(null)
  }

  const handleAdd = async () => {
    if (!name.trim()) return
    await create.mutateAsync({
      name: name.trim(),
      default_quantity: Number(qty) || 1,
      unit_cost: Number(unitCost) || 0,
    })
    resetForm()
  }

  const handleSaveEdit = async (id: string) => {
    if (!name.trim()) return
    await update.mutateAsync({
      id,
      name: name.trim(),
      default_quantity: Number(qty) || 1,
      unit_cost: Number(unitCost) || 0,
    })
    resetForm()
  }

  const handleDelete = (id: string, presetName: string) => {
    if (window.confirm(`Delete "${presetName}" from the library?`)) {
      del.mutate(id)
    }
  }

  const startEdit = (preset: { id: string; name: string; default_quantity: number; unit_cost: number }) => {
    setEditingId(preset.id)
    setName(preset.name)
    setQty(String(preset.default_quantity))
    setUnitCost(String(preset.unit_cost))
    setAdding(false)
  }

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/settings" />
      <h1 style={{ marginBottom: 'var(--space-4)' }}>Hardware library</h1>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-4)' }}>
        Save common hardware items (keyrings, magnets, key caps) here so you can pick
        them quickly when calculating or logging a print, instead of typing the name +
        cost every time.
      </p>

      {isLoading ? (
        <Skeleton height={60} />
      ) : (
        <>
          {(presets ?? []).length === 0 && !adding && (
            <Card style={{ marginBottom: 'var(--space-4)' }}>
              <div style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-4)' }}>
                No items yet. Tap &quot;+ Add&quot; below to save your first one.
              </div>
            </Card>
          )}

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(presets ?? []).map((p) =>
              editingId === p.id ? (
                <li key={p.id} style={{ marginBottom: 'var(--space-2)' }}>
                  <PresetEditor
                    name={name}
                    qty={qty}
                    unitCost={unitCost}
                    onName={setName}
                    onQty={setQty}
                    onUnitCost={setUnitCost}
                    onSave={() => handleSaveEdit(p.id)}
                    onCancel={resetForm}
                    saving={update.isPending}
                  />
                </li>
              ) : (
                <li key={p.id} style={{ marginBottom: 'var(--space-2)' }}>
                  <Card padding="var(--space-3)">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                          {p.default_quantity} × {formatMoney(Number(p.unit_cost))}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => startEdit(p)}
                        style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id, p.name)}
                        style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}
                      >
                        Delete
                      </button>
                    </div>
                  </Card>
                </li>
              ),
            )}
          </ul>

          {adding ? (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <PresetEditor
                name={name}
                qty={qty}
                unitCost={unitCost}
                onName={setName}
                onQty={setQty}
                onUnitCost={setUnitCost}
                onSave={handleAdd}
                onCancel={resetForm}
                saving={create.isPending}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              style={{
                marginTop: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-5)',
                background: 'var(--color-primary)',
                color: 'white',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: 'var(--font-size-sm)',
                width: '100%',
              }}
            >
              + Add hardware item
            </button>
          )}
        </>
      )}
    </div>
  )
}

function PresetEditor({
  name,
  qty,
  unitCost,
  onName,
  onQty,
  onUnitCost,
  onSave,
  onCancel,
  saving,
}: {
  name: string
  qty: string
  unitCost: string
  onName: (v: string) => void
  onQty: (v: string) => void
  onUnitCost: (v: string) => void
  onSave: () => void
  onCancel: () => void
  saving: boolean
}) {
  const inputStyle = {
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-sm)',
    width: '100%',
  } as const

  return (
    <Card padding="var(--space-3)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <input
          type="text"
          placeholder="Name (e.g., Keyring)"
          value={name}
          onChange={(e) => onName(e.target.value)}
          style={inputStyle}
          autoFocus
        />
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
              Default qty / unit
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={qty}
              onChange={(e) => onQty(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600 }}>
              $ each
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={unitCost}
              onChange={(e) => onUnitCost(e.target.value)}
              style={inputStyle}
            />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !name.trim()}
            style={{
              flex: 1,
              padding: 'var(--space-2) var(--space-4)',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </Card>
  )
}
